import AnimateInView from '@atoms/AnimateInView/AnimateInView';
import Image from '@atoms/Image/Image';
import TextContent from '@atoms/TextContent/TextContent';
import Video from '@atoms/Video/Video';
import PositioningEnum from '@models/enums/PositioningEnum';
import SiteEnum from '@models/enums/SiteEnum';
import ThemeEnum from '@models/enums/ThemeEnum';
import VerticalAlignmentEnum from '@models/enums/VerticalAlignmentEnum';
import { IStorytellingModule } from '@models/IStorytellingModule';
import { VideoButton } from '@molecules/VideoButton/VideoButton';
import classNames from 'classnames';

const StorytellingModule = ({
  autoplay = false,
  backgroundColour,
  ctas,
  hasPlayButton,
  header,
  imageBlock,
  playButtonLabel,
  positioning,
  posterBlock,
  site,
  subText,
  textAlignment,
  theme = ThemeEnum.Light,
  trumpetText,
  usePortraitMedia,
  video,
  videoConfig,
  wrapperClasses = 'tw-container',
  youtubeVideo,
}: IStorytellingModule) => {
  const bgB2B = site === SiteEnum.B2B && backgroundColour;
  const landscapeAspectRatio = site === SiteEnum.B2B ? 1.6 : 4 / 3;
  const mediaAspectRatio = usePortraitMedia ? 4 / 5 : landscapeAspectRatio;

  const youtubeVideoUrl = youtubeVideo?.videoId
    ? `https://www.youtube.com/watch?v=${youtubeVideo.videoId}-nocookie`
    : null;

  const shouldAutoplay = autoplay && !!video?.videoSource;

  return (
    <section>
      <AnimateInView
        className={classNames(wrapperClasses, 'article-bleed')}
        element="div"
      >
        <div
          className="tw-grid tw-gap-sm md:tw-grid-cols-6 lg:tw-grid-cols-12 xl:tw-gap-md"
          style={{
            backgroundColor: backgroundColour ? backgroundColour : undefined,
          }}
        >
          <div
            className={classNames(
              'tw-relative tw-flex tw-items-start md:tw-col-span-3 lg:tw-col-span-6',
              positioning === PositioningEnum.Left
                ? 'md:tw-order-0 md:tw-col-start-1'
                : 'md:tw-order-1 md:tw-col-start-4 lg:tw-col-start-7'
            )}
          >
            {hasPlayButton && video && videoConfig ? (
              <div className="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2">
                <VideoButton
                  darkFont={false}
                  playButtonLabel={playButtonLabel ?? 'play'}
                  videoSource={video.videoSource}
                  videoConfig={videoConfig}
                />
              </div>
            ) : null}
            {(youtubeVideoUrl || video) && !videoConfig && (
              <Video
                poster={posterBlock?.image || imageBlock?.image || undefined}
                videoSource={youtubeVideoUrl || video?.videoSource || ''}
                modalLabels={youtubeVideo?.videoModalLabels}
                isPortrait={usePortraitMedia}
                videoAttributes={{
                  playsinline: shouldAutoplay,
                  muted: shouldAutoplay,
                  playing: shouldAutoplay,
                  loop: shouldAutoplay,
                }}
              />
            )}
            {imageBlock?.image?.url && (
              <Image
                imageClasses={bgB2B ? 'tw-h-full tw-object-cover' : undefined}
                classes={bgB2B ? 'tw-h-full' : undefined}
                image={imageBlock?.image}
                alternativeText={imageBlock?.alternativeText}
                sizes="(max-width: 752px) 100vw, 50vw"
                aspectRatio={mediaAspectRatio}
              />
            )}
          </div>
          <div
            className={classNames(
              'tw-flex md:tw-col-span-3',
              bgB2B && 'tw-pb-6 max-md:tw-px-4 md:tw-py-20',
              textAlignment === VerticalAlignmentEnum.Top
                ? 'tw-items-start'
                : 'tw-items-center',
              positioning === PositioningEnum.Left
                ? 'md:tw-order-1 md:tw-col-start-4 lg:tw-col-start-8'
                : 'md:tw-order-0 md:tw-col-start-1',
              backgroundColour
                ? 'tw-px-sm tw-py-md lg:tw-col-span-4 lg:tw-px-0'
                : 'lg:tw-col-span-5',
              {
                'lg:tw-col-start-2':
                  backgroundColour && positioning === PositioningEnum.Right,
                'md:tw-pr-12': bgB2B && positioning === PositioningEnum.Left,
                'md:tw-pl-12': bgB2B && positioning === PositioningEnum.Right,
              }
            )}
          >
            <TextContent
              site={site}
              textContent={{
                ctas,
                header,
                subText,
                trumpetText,
              }}
              theme={theme}
            />
          </div>
        </div>
      </AnimateInView>
    </section>
  );
};

export default StorytellingModule;
